import { useLocation } from 'react-router';
import { RefObject, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAutomaticSpocSwitch } from '../../store/authentication/actions';
import { useAuthentication } from '../../app-store/authentication/useAuthentication';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface SetSearchParamEvent {
    version: number;
    paramName: string;
    newValue: string | undefined;
}

interface NavigateToEvent {
    version: number;
    pathName: string;
    searchParams?: string;
}

const EVENT_SET_QUERY_PARAM = 'setQueryParam';
const EVENT_NAVIGATE_TO = 'navigateTo';
const EVENT_TOKEN_CHANGED = 'tokenChanged';

const isNavigateToEventV1 = (arg: any): arg is NavigateToEvent => {
    return arg.version === 1 && arg.pathName !== undefined;
};

const isSetQueryParamV1 = (arg: any): arg is SetSearchParamEvent => {
    return arg.version === 1 && arg.paramName !== undefined;
};

function isCustomEvent(arg: any): arg is CustomEvent {
    return arg.detail !== undefined;
}

function dispatchTokenEvent(customElementRef: RefObject<HTMLDivElement>) {
    const event = new CustomEvent(EVENT_TOKEN_CHANGED, {
        detail: {
            version: 1,
            token: localStorage.getItem('token')
        }
    });

    customElementRef.current?.dispatchEvent(event);
}

export const ReportingPage = () => {
    const { i18n } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { spoc } = useAuthentication();
    const customElementRef = useRef<HTMLDivElement>(null);

    // this hook is responsible for switching the spoc according to the search param "spocId"
    useAutomaticSpocSwitch();

    useEffect(() => {
        const handleNavigateToEvent = (event: any) => {
            if (isCustomEvent(event) && isNavigateToEventV1(event.detail)) {
                navigate({ pathname: event.detail.pathName, search: event.detail.searchParams });
            }
        };

        const handleSetSearchParamEvent = (event: any) => {
            if (isCustomEvent(event) && isSetQueryParamV1(event.detail)) {
                setSearchParams(
                    current => {
                        if (event.detail.newValue) {
                            current.set(event.detail.paramName, event.detail.newValue);
                        } else {
                            current.delete(event.detail.paramName);
                        }
                        return current;
                    },
                    { replace: true }
                );
            }
        };

        setInterval(() => {
            dispatchTokenEvent(customElementRef);
        }, 3000);
        dispatchTokenEvent(customElementRef);
        const currentElement = customElementRef.current;
        currentElement && currentElement.addEventListener(EVENT_NAVIGATE_TO, handleNavigateToEvent);
        currentElement && currentElement.addEventListener(EVENT_SET_QUERY_PARAM, handleSetSearchParamEvent);

        return () => {
            currentElement && currentElement.removeEventListener(EVENT_NAVIGATE_TO, handleNavigateToEvent);
            currentElement && currentElement.removeEventListener(EVENT_SET_QUERY_PARAM, handleSetSearchParamEvent);
        };
    }, [searchParams, navigate, setSearchParams]);

    const partnerId = (spoc.id.startsWith('PARTNER') ? spoc.id : spoc?.partnerId)?.replace('PARTNER', '');
    const siteId = spoc.id.startsWith('SITE') ? spoc.id.replace('SITE', '') : undefined;

    return (
        <div className="min-h-screen">
            <business-intelligence
                ref={customElementRef}
                pathname={location.pathname}
                searchparams={searchParams.toString()}
                partnerId={partnerId}
                siteId={siteId}
                language={i18n.language}
            ></business-intelligence>
        </div>
    );
};
